import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { NavDropdownItem } from '@/components/shared/Navbar';

/**
 * The DESKTOP half of a marketing product menu ("Law School ▾", "Bar Prep ▾").
 *
 * ONE COMPONENT, TWO MENUS, deliberately. The previous shape of this repo is the
 * argument: four separate navbars each hand-rolled their own
 * DropdownMenu/Trigger/Content block, and when the `#outlines` anchor died it
 * died in several of them independently. The mobile half is already shared —
 * Navbar.tsx renders `mobileChildren` as a flat indented list — so this closes
 * the other half.
 *
 * WIDTH IS w-80, NOT THE w-56 THE ANCHOR MENUS USE. The designer offered both
 * ("widen to ~w-80 with two-line items, or keep w-56 and drop the
 * descriptions"); w-80 is taken because the descriptions here are PRICES and
 * AVAILABILITY DATES — "$20/month", "From $499", "March 2027" — which is the
 * information a visitor is choosing between. Dropping them to fit w-56 would
 * make the menu a list of five product names with no way to tell a free tier
 * from a $499 one. The narrow w-56 anchor menus keep their own shape; this is
 * not applied to them.
 *
 * Items are `<Link>`, not `<a>` with a scroll handler: every entry in these
 * menus is a REAL ROUTE. That is the durable fix the 2026-09-11 redesign is
 * built on — anchors into a page whose sections move are inherently fragile,
 * which is how `#outlines`, `#study` and `#courses` died unnoticed.
 */
export function MarketingNavDropdown({
  label,
  items,
  isActive = false,
}: {
  label: string;
  items: NavDropdownItem[];
  /** Lights the trigger when the visitor is already inside this product family. */
  isActive?: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className={`relative flex min-h-[44px] items-center gap-2 px-3 py-2 transition-colors after:absolute after:bottom-0 after:left-0 after:h-[2px] after:bg-[#F37022] after:transition-all ${
            isActive
              ? 'text-[#F37022] after:w-full'
              : 'text-gray-700 dark:text-gray-300 hover:text-[#F37022] after:w-0 hover:after:w-full'
          }`}
        >
          {label}
          <ChevronDown size={16} aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-80 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
      >
        {items.map((item) => (
          <div key={item.href}>
            {item.separatorBefore && <DropdownMenuSeparator />}
            <DropdownMenuItem
              asChild
              className="hover:bg-[#F37022]/10 focus:bg-[#F37022]/10 hover:text-[#F37022] focus:text-[#F37022]"
            >
              <Link
                to={item.href}
                className="flex min-h-[44px] cursor-pointer items-start gap-2 text-gray-700 dark:text-gray-300"
                onClick={() => setOpen(false)}
              >
                {item.icon && <span className="mt-0.5 flex-none">{item.icon}</span>}
                <span className="flex flex-col">
                  <span className="font-medium">{item.label}</span>
                  {item.description && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {item.description}
                    </span>
                  )}
                </span>
              </Link>
            </DropdownMenuItem>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default MarketingNavDropdown;
